/**
 * #939 — which open tabs show a path through a folder that was just renamed or moved.
 *
 * A tab's breadcrumb is the file's full path, refreshed only when the FILE itself changes, so
 * renaming a folder above it left the old folder name in the tab's path. The folder ids on a
 * tab's path come from its cached breadcrumbs (the file info stores them).
 *
 * The cached breadcrumbs are trusted only when they spell the tab's current path: they can
 * be stale (one API instance answers from its own path cache, while the tab's `path` is read
 * from the database), and a stale list would wrongly rule a tab out. A tab whose breadcrumbs
 * aren't cached — or don't match — can't be ruled out, so it is refreshed (which re-caches
 * them). A tab opened through a same-tenant `.chain` carries compound ids (`s_<chain>_<id>`)
 * for the folders past the chain, while the rename event names the folder's own id — those
 * match by their last segment.
 *
 * Known limit (accepted): for a compound-id tab the server builds `path` FROM the same
 * breadcrumbs, so a stale answer from another API instance's path cache (up to 30s, a folder
 * renamed ABOVE the mount row) matches itself and can't be detected here; it heals on the
 * next event under it or when the tab is reopened. A fix belongs server-side (read the
 * recipient-side crumbs from the database for a mount child).
 */
export interface TabPathInfo { fileId: string; path?: string }

/**
 * A realtime event that can change the path under a folder: a folder renamed or moved. Renames
 * arrive as `file:moved` from the drive, but as `file:updated` from a share-link visitor or a
 * rename inside a received `.shared` — both count. An agent `mv` that minted a new row
 * (`previousFileId`) is handled elsewhere; a plain file's event can't change anyone's path.
 */
export function isFolderPathEvent(e: { type: string; previousFileId?: string; file?: { isFolder?: boolean } }): boolean {
  return (e.type === 'file:moved' || e.type === 'file:updated') && !e.previousFileId && e.file?.isFolder !== false
}

export function tabsUnderFolders(
  tabs: readonly TabPathInfo[],
  folderIds: ReadonlySet<string>,
  cachedCrumbs: (fileId: string) => ReadonlyArray<{ id: string; name?: string }> | undefined,
): string[] {
  const out = new Set<string>()
  const isMovedFolder = (id: string) => folderIds.has(id) ||
    (id.startsWith('s_') && [...folderIds].some(f => id.endsWith(`_${f}`)))
  for (const tab of tabs) {
    const crumbs = cachedCrumbs(tab.fileId)
    if (!crumbs || !crumbsMatchPath(crumbs, tab.path)) { out.add(tab.fileId); continue }
    if (crumbs.some(c => c.id !== tab.fileId && isMovedFolder(c.id))) out.add(tab.fileId)
  }
  return [...out]
}

/** The breadcrumbs spell the tab's path (`/papan/<names…>`) — else they are stale. */
function crumbsMatchPath(crumbs: ReadonlyArray<{ name?: string }>, path: string | undefined): boolean {
  if (!path) return false
  return `/papan/${crumbs.map(c => c.name ?? '').join('/')}` === path
}

/**
 * #823 — which open tabs show a file that a `file:deleted` event took to the bin: the deleted item itself, or a file
 * under it. The event names only the deleted ROOT, so a binned parent folder reaches its files only through the tabs'
 * cached breadcrumbs. Trusted breadcrumbs answer for sure (`under`); a tab whose breadcrumbs are missing or stale
 * can't be ruled in or out (`unsure` — the caller asks the server whether the file is still there). Compound
 * (`s_<chain>_<id>`) ids match by their last segment, as in {@link tabsUnderFolders}.
 */
export function tabsUnderDeleted(
  tabs: readonly TabPathInfo[],
  deletedIds: ReadonlySet<string>,
  cachedCrumbs: (fileId: string) => ReadonlyArray<{ id: string; name?: string }> | undefined,
): { under: string[]; unsure: string[] } {
  const isDeleted = (id: string) => deletedIds.has(id) ||
    (id.startsWith('s_') && [...deletedIds].some(f => id.endsWith(`_${f}`)))
  const under = new Set<string>()
  const unsure = new Set<string>()
  for (const tab of tabs) {
    if (isDeleted(tab.fileId)) { under.add(tab.fileId); continue }
    const crumbs = cachedCrumbs(tab.fileId)
    if (!crumbs || !crumbsMatchPath(crumbs, tab.path)) { unsure.add(tab.fileId); continue }
    if (crumbs.some(c => isDeleted(c.id))) under.add(tab.fileId)
  }
  return { under: [...under], unsure: [...unsure] }
}

/**
 * #823 — is a greyed tab's file back (restored)? A POINTER row's own answer is not enough: a `.shared` / `.chain` row
 * still answers after its TARGET went to the bin (the room closed on the target). The detail carries the kernel's
 * answer for a pointer row — `pointerLive`: it leads somewhere readable now (a target binned, a pending chain, a cut
 * placement, a foreign route gone: no). A plain file (or a compound id, which fails once its mount is gone) answers
 * for itself. Any failure = not back.
 */
export async function tabFileIsBack(
  fileId: string,
  getInfo: (id: string) => Promise<unknown>,
): Promise<boolean> {
  try {
    return detailIsBack(await getInfo(fileId))
  } catch {
    return false
  }
}

/** #823: does a detail the server ANSWERED describe a file that is back? A pointer row answers 200 even while its
 *  target is in the bin — only a live pointer is back. */
export function detailIsBack(info: unknown): boolean {
  const d = info as { fileType?: string | null; pointerLive?: boolean }
  if (d.fileType === 'shared' || d.fileType === 'chain') return d.pointerLive === true
  return true
}
